import * as Sentry from '@sentry/vue';
import {
  beforeBreadcrumb,
  beforeSendTransaction,
  initialScope,
  beforeSend,
  tracesSampler,
} from '@/util/sentry/eventProcessing';
import { beforeErrorSampling } from '@/util/sentry/replay';
import type { Router } from 'vue-router';
import type { App } from 'vue';

export interface SentryConfig {
  app: App;
  router: Router;
  enabled?: boolean;
  dsn?: string;
  environment: string;
  tunnelUrl: string;
  release?: string;
  replaysEnabled?: boolean;
}

export function initialiseSentry(config: SentryConfig): void {
  const { enabled, app, dsn, environment, tunnelUrl, release, router, replaysEnabled } = config;

  const integrations = [Sentry.browserTracingIntegration({ router })];
  if (replaysEnabled) {
    // Skip replays for timeout / network errors — environmental noise, not bugs.
    integrations.push(Sentry.replayIntegration({ beforeErrorSampling }));
  }

  Sentry.init({
    app,
    enabled,
    environment,
    dsn,
    tunnel: tunnelUrl,
    integrations,
    //https://docs.sentry.io/platforms/javascript/configuration/sampling/
    // performance tracing — drops noise (favicon, mixpanel, etc), trims noisy
    // third parties (maps/gstatic) and samples everything else at 25%.
    tracesSampler,
    //error tracing
    sampleRate: 1,
    // Session Replay
    replaysSessionSampleRate: 0, // Do not record replays for non-error sessions; replaysOnErrorSampleRate still captures sessions where errors occur.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    normalizeDepth: 10,
    initialScope,
    beforeSend,
    beforeSendTransaction,
    beforeBreadcrumb,
    release,
  });
}
